'use client';

import React, { useCallback, useRef, useState } from 'react';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';

interface BeforeAfterSliderProps {
  before: string;
  after: string;
  /** Accessible label describing the transformation. */
  alt: string;
  className?: string;
}

/**
 * Interactive before/after comparison slider. Drag (or use the arrow keys) to
 * reveal the edited "after" image over the original "before" image.
 */
export default function BeforeAfterSlider({ before, after, alt, className = '' }: BeforeAfterSliderProps) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 4));
    if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden rounded-2xl border border-white/[0.12] select-none touch-none ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      {/* AFTER (base layer) */}
      <div className="relative w-full h-full">
        <ProtectedAppImage src={after} alt={`${alt} — after editing`} className="w-full h-full object-cover" />
        <span className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 text-xs font-bold rounded shadow-lg shadow-purple-500/30 pointer-events-none">
          AFTER
        </span>
      </div>

      {/* BEFORE (clipped overlay) */}
      <div
        className="absolute inset-0 h-full overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <div className="relative h-full" style={{ width: containerRef.current?.offsetWidth || '100%' }}>
          <ProtectedAppImage src={before} alt={`${alt} — before editing`} className="w-full h-full object-cover" />
          <span className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-3 py-1 text-xs font-bold rounded pointer-events-none">
            BEFORE
          </span>
        </div>
      </div>

      {/* Handle */}
      <div
        role="slider"
        tabIndex={0}
        aria-label={`${alt} before and after comparison`}
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
        onKeyDown={onKeyDown}
        className="absolute top-0 bottom-0 -ml-0.5 w-1 bg-white/90 cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-purple-400"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7l-5 5 5 5M16 7l5 5-5 5" />
          </svg>
        </div>
      </div>
    </div>
  );
}
